import { FC, useRef, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { Trash2, Calendar, Pencil, Check } from "lucide-react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  deleteUserNoteAction,
  getUserNotesByIdAction,
  updateUserNoteAction,
} from "../../../redux/actions/customerAction";
import { DeleteConfirmationModal } from "../../common/delete-confirmation-modal/delete-confirmation-modal";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { formatDate } from "../../../util/common-functions";
import {
  NoteOccasion,
  NoteOccasionLabels,
} from "../../../typings/enum/noteOccasions";
import { Colors } from "../../../configs";

export interface INotesCardProps {
  noteId: number;
  description: string;
  noteBy: string;
  lastEditedBy: string;
  date: Date | null;
  occasions: NoteOccasion[];
}

function SoftCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onChange}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        borderRadius: "0.75rem",
        px: "0.75rem",
        py: "0.5rem",
        textAlign: "left",
        background: "none",
        border: "none",
        cursor: "pointer",
        transition: "background-color 0.15s",
        "&:hover": {
          backgroundColor: Colors.WHITE,
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "1.25rem",
          height: "1.25rem",
          flexShrink: 0,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "0.375rem",
          border: `1.5px solid ${checked ? Colors.PRIMARY : Colors.SECONDARY}`,
          backgroundColor: checked ? Colors.PRIMARY : Colors.WHITE,
          transition: "all 0.15s",
        }}
      >
        {checked && (
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m20 6-11 11-5-5" />
          </svg>
        )}
      </Box>
      <Typography
        sx={{ fontSize: "var(--fs-base)", fontWeight: 500, color: Colors.MAIN_GRAY }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const UserNotesCard: FC<INotesCardProps> = ({
  noteId,
  description,
  noteBy,
  lastEditedBy,
  date,
  occasions,
}) => {
  const [note, setNote] = useState(description);
  const [selectedOccasions, setSelectedOccasions] =
    useState<NoteOccasion[]>(occasions);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();
  const params = useParams();
  const customerId = params.id;
  const { user } = useAppSelector((state) => state.auth);
  const snackbar = useSnackbarContext();

  const handleExpandChange = (force?: boolean) => {
    const next = force ?? !expanded;
    setExpanded(next);
    requestAnimationFrame(() => {
      if (!contentRef.current) return;
      if (next) {
        contentRef.current.style.maxHeight =
          contentRef.current.scrollHeight + "px";
        const scroller = cardRef.current?.closest(
          "[data-notes-scroll]"
        ) as HTMLElement | null;
        if (scroller && cardRef.current) {
          scroller.scrollTo({
            top: cardRef.current.offsetTop - 16,
            behavior: "smooth",
          });
        } else {
          cardRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      } else {
        contentRef.current.style.maxHeight = "0px";
      }
    });
  };

  const handleCheckboxChange = (key: string) => {
    setSelectedOccasions((prev) =>
      prev.includes(key as NoteOccasion)
        ? prev.filter((k) => k !== key)
        : [...prev, key as NoteOccasion]
    );
  };

  const handleOnUpdateNote = async () => {
    if (note.trim().length === 0) {
      snackbar.showSnackbar("Cannot add empty notes", "warning");
      return;
    }
    if (customerId && user) {
      await dispatch(
        updateUserNoteAction({
          id: noteId,
          note,
          occasions: selectedOccasions,
          merchantUserId: 1,
          customerId: Number(customerId),
        })
      );
      dispatch(getUserNotesByIdAction(customerId as string));
      handleExpandChange(false);
    }
  };

  const handleOnDeleteNote = async () => {
    if (customerId && user) {
      await dispatch(deleteUserNoteAction(noteId.toString()));
      dispatch(getUserNotesByIdAction(customerId as string));
    }
  };

  return (
    <>
      <DeleteConfirmationModal
        open={deleteConfirmation}
        onCancel={() => setDeleteConfirmation(false)}
        onConfirm={handleOnDeleteNote}
      />

      <Box ref={cardRef}>
        {/* Note row */}
        <Box
          sx={{
            borderRadius: "1.25rem",
            border: `1px solid ${Colors.ACCENT_GRAY}`,
            backgroundColor: Colors.WHITE,
            p: "1rem",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            transition: "transform 0.15s, box-shadow 0.15s",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            {/* Delete */}
            <Box
              component="button"
              type="button"
              aria-label="Delete note"
              onClick={() => setDeleteConfirmation(true)}
              sx={{
                display: "flex",
                width: "2.25rem",
                height: "2.25rem",
                flexShrink: 0,
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "0.75rem",
                backgroundColor: "#FEF2F2",
                color: Colors.BROWN,
                border: "none",
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                transition: "background-color 0.15s",
                "&:hover": { backgroundColor: "#FEE2E2" },
              }}
            >
              <Trash2 size={17} />
            </Box>

            {/* Content */}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Box
                  sx={{
                    borderRadius: "2rem",
                    backgroundColor: `${Colors.SECONDARY}1A`,
                    px: "0.625rem",
                    py: "0.25rem",
                    fontSize: "var(--fs-2xs)",
                    fontWeight: 700,
                    color: Colors.SECONDARY,
                    flexShrink: 0,
                  }}
                >
                  Repeatedly
                </Box>
                <Typography
                  sx={{
                    fontSize: "var(--fs-base)",
                    fontWeight: 600,
                    color: Colors.TEXT_GRAY_DARK,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {description}
                </Typography>
              </Box>
              <Box
                sx={{
                  mt: "0.5rem",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "0.25rem",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "var(--fs-2xs)",
                    fontWeight: 500,
                    color: Colors.SECONDARY,
                  }}
                >
                  <Box component="span" sx={{ color: `${Colors.MAIN_GRAY}99` }}>
                    Added by
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: `${Colors.MAIN_GRAY}66`, px: "0.5rem" }}
                  >
                    :
                  </Box>
                  {noteBy}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "var(--fs-2xs)",
                    fontWeight: 500,
                    color: Colors.SECONDARY,
                  }}
                >
                  <Box component="span" sx={{ color: `${Colors.MAIN_GRAY}99` }}>
                    Last edited by
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: `${Colors.MAIN_GRAY}66`, px: "0.5rem" }}
                  >
                    :
                  </Box>
                  {lastEditedBy}
                </Typography>
              </Box>
            </Box>

            {/* Date + Edit */}
            <Box
              sx={{
                display: "flex",
                flexShrink: 0,
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              {date && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    borderRadius: "2rem",
                    backgroundColor: Colors.WHITE,
                    px: "0.75rem",
                    py: "0.5rem",
                    fontSize: "var(--fs-base)",
                    fontWeight: 600,
                    color: Colors.SECONDARY,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    border: `1px solid ${Colors.ACCENT_GRAY}`,
                  }}
                >
                  <Calendar size={16} />
                  {formatDate(date)}
                </Box>
              )}
              <Box
                component="button"
                type="button"
                aria-label="Edit note"
                onClick={() => handleExpandChange()}
                sx={{
                  display: "flex",
                  width: "2.25rem",
                  height: "2.25rem",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "0.75rem",
                  backgroundColor: Colors.SECONDARY,
                  color: Colors.WHITE,
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,121,194,0.3)",
                  transition: "filter 0.15s",
                  "&:hover": { filter: "brightness(0.95)" },
                  "&:active": { transform: "scale(0.95)" },
                }}
              >
                <Pencil size={17} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Expandable edit section */}
        <Box
          ref={contentRef}
          sx={{
            overflow: "hidden",
            transition: "max-height 0.4s ease-in-out",
          }}
          style={{ maxHeight: 0 }}
        >
          <Box
            sx={{
              mt: "0.75rem",
              borderRadius: "1.25rem",
              backgroundColor: Colors.BACKGROUND_GRAY,
              p: "1.25rem",
              border: `1px solid ${Colors.ACCENT_GRAY}`,
            }}
          >
            <Box
              component="textarea"
              value={note}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setNote(e.target.value)
              }
              sx={{
                height: "6rem",
                width: "100%",
                resize: "none",
                borderRadius: "1rem",
                border: `1px solid ${Colors.ACCENT_GRAY}`,
                backgroundColor: Colors.WHITE,
                px: "1rem",
                py: "0.75rem",
                fontSize: "var(--fs-base)",
                color: Colors.MAIN_GRAY,
                boxShadow: "inset 0 1px 3px rgba(0,0,0,0.06)",
                outline: "none",
                fontFamily: "var(--font-sans)",
                display: "block",
                transition: "border-color 0.15s, box-shadow 0.15s",
                "&::placeholder": { color: Colors.ACCENT_GRAY_DISABLED },
                "&:focus": {
                  borderColor: Colors.SECONDARY,
                  boxShadow: `inset 0 1px 3px rgba(0,0,0,0.06), 0 0 0 3px ${Colors.SECONDARY}18`,
                },
              }}
            />

            <Box sx={{ mt: "1rem" }}>
              <Typography
                sx={{
                  fontSize: "var(--fs-base)",
                  fontWeight: 700,
                  color: Colors.MAIN_GRAY,
                }}
              >
                When to show
              </Typography>
              <Box
                sx={{
                  mt: "0.5rem",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  columnGap: "2rem",
                  rowGap: "0.25rem",
                  borderRadius: "1rem",
                  backgroundColor: `${Colors.ACCENT_GRAY}33`,
                  p: "0.75rem",
                }}
              >
                {Object.keys(NoteOccasion).map((key) => (
                  <SoftCheckbox
                    key={key}
                    label={NoteOccasionLabels[key as keyof typeof NoteOccasion]}
                    checked={selectedOccasions.includes(key as NoteOccasion)}
                    onChange={() => handleCheckboxChange(key)}
                  />
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                mt: "1rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "0.75rem",
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => setDeleteConfirmation(true)}
                sx={{
                  display: "flex",
                  width: "2.25rem",
                  height: "2.25rem",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "0.75rem",
                  border: `1px solid ${Colors.ACCENT_GRAY}`,
                  backgroundColor: Colors.WHITE,
                  color: Colors.BROWN,
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                  "&:hover": { backgroundColor: "#FEF2F2" },
                }}
              >
                <Trash2 size={16} />
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => {
                  setNote(description);
                  setSelectedOccasions(occasions);
                  handleExpandChange(false);
                }}
                sx={{
                  borderRadius: "1rem",
                  border: `1px solid ${Colors.ACCENT_GRAY}`,
                  backgroundColor: Colors.WHITE,
                  px: "1.25rem",
                  py: "0.5rem",
                  fontSize: "var(--fs-base)",
                  fontWeight: 700,
                  color: Colors.MAIN_GRAY,
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                  "&:hover": { backgroundColor: Colors.BACKGROUND_GRAY },
                  "&:active": { transform: "scale(0.98)" },
                }}
              >
                Cancel
              </Box>
              <Box
                component="button"
                type="button"
                disabled={note.trim().length === 0}
                onClick={handleOnUpdateNote}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  borderRadius: "1rem",
                  px: "1.25rem",
                  py: "0.5rem",
                  fontSize: "var(--fs-base)",
                  fontWeight: 700,
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  backgroundColor: Colors.PRIMARY,
                  color: Colors.WHITE,
                  boxShadow: "0 4px 12px rgba(1,135,82,0.28)",
                  "&:hover": { filter: "brightness(0.95)" },
                  "&:active": { transform: "scale(0.98)" },
                  "&:disabled": {
                    cursor: "not-allowed",
                    backgroundColor: Colors.ACCENT_GRAY,
                    color: `${Colors.MAIN_GRAY}72`,
                    boxShadow: "none",
                  },
                }}
              >
                <Check size={16} />
                Update
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              mx: "auto",
              my: "0.5rem",
              height: "1px",
              width: "5rem",
              backgroundColor: Colors.ACCENT_GRAY,
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default UserNotesCard;
